import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { __MANAGEMENT_API_URL__ } from 'shell/constants/environment';
import Table from 'shell/components/shared/table';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { generatePath, useNavigate } from 'react-router-dom';
import { HttpResponseList } from 'shell/models/http-response';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { initialValuesFunctions } from '..';
import { useFormik } from 'formik';
import { FiltersFunctions, FunctionsClient } from './types';
import { transformDataset } from './utils';
import { AlertModal } from 'system/components/shared/delete-alert/types';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import DeleteSuccessModal from 'system/components/shared/delete-success';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import FilterFunctions from 'system/components/functions/list/filter-functions';
import { Functionality } from 'shell/models/functionality';
import { CommonFunctionsCrumbs } from '../constants';
import { useActionAuthorization } from 'shell/hooks/use-action-authorization';
import { authsObj, headCells, validationSchemaToponymyFilter } from './constants';
import ListHeader from 'shell/components/shared/list-header';
import { Typography } from '@mui/material';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';

const FunctionsList = (): React.ReactElement => {
  const [deleteAlertModal, setDeleteAlertModal] = useState<AlertModal<FunctionsClient>>({
    element: null,
    open: false,
  });

  const [deleteSuccessModal, setDeleteSuccessModal] = useState<AlertModal<FunctionsClient>>({
    element: null,
    open: false,
  });

  const deleteFunction = useHttpClient(`${__MANAGEMENT_API_URL__}/funzioni`, {
    options: {
      method: 'DELETE',
      errorCatching: true,
      loading: true,
    },
  });

  const [functions, getFunctions] = useHttpFetch<
    HttpResponseList<Functionality>,
    Array<FunctionsClient>
  >(`${__MANAGEMENT_API_URL__}/funzioni`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'GET',
      loading: true,
      data: [],
    },
    transformer: (response) => {
      return response.data?.payload?.list.map((item) => {
        const itemResult: FunctionsClient = {
          id: item.key?.codice?.toString(),
          function: item.nome,
          entity: item.entita,
        };
        return itemResult;
      });
    },
  });

  const filterTable = useFilterTable<FiltersFunctions>();

  const onSubmit = useCallback(
    (formValues: FiltersFunctions) => {
      const queryString = transformDataset({ ...formValues, page: 0 });
      filterFunctionsForm.setFieldValue('page', 0);
      getFunctions({
        params: {},
        paramsSerializer: () => queryString,
      });
    },
    [getFunctions]
  );

  const filterFunctionsForm = useFormik({
    initialValues: initialValuesFunctions,
    validationSchema: validationSchemaToponymyFilter,
    onSubmit,
  });

  useEffect(() => {
    filterFunctionsForm.setValues(filterTable.values || initialValuesFunctions);
    const queryString = transformDataset(filterTable.values || initialValuesFunctions);
    getFunctions({
      params: {},
      paramsSerializer: () => queryString,
    });
  }, []);

  useEffect(() => {
    return () => filterTable.setValues(filterFunctionsForm.values);
  }, [filterFunctionsForm.values]);

  const count: number = useMemo(() => {
    const dataList = (functions.data as Array<FunctionsClient>) || [];
    return dataList?.length || 0;
  }, [functions.data]);

  const navigate = useNavigate();

  const auths = useActionAuthorization(authsObj);

  const onVisibilityPress = useCallback(
    (cell): Promise<FunctionsClient> => {
      return new Promise((resolve) => {
        navigate(generatePath('/sistema/funzioni/dettaglio/:id', { id: cell.id.toString() }));
        resolve(cell);
      });
    },
    [navigate]
  );

  const onEditPress = useCallback(
    (cell): Promise<FunctionsClient> => {
      return new Promise((resolve) => {
        navigate(generatePath('/sistema/funzioni/modifica/:id', { id: cell.id.toString() }));
        resolve(cell);
      });
    },
    [navigate]
  );

  const onTrashPress = useCallback(
    (cell): Promise<FunctionsClient> => {
      return new Promise((resolve) => {
        setDeleteAlertModal({ open: true, element: cell });
        resolve(cell);
      }) as Promise<FunctionsClient>;
    },
    [setDeleteAlertModal]
  );

  const handleChangePage = useCallback(
    (newPage) => {
      filterFunctionsForm.setFieldValue('page', newPage);
    },
    [filterFunctionsForm.setFieldValue]
  );

  const handleChangeRowsPerPage = useCallback(
    (rowsPerPage) => {
      const newDataset = {
        ...filterFunctionsForm.values,
        rowsPerPage: rowsPerPage || 0,
        page: 0,
      };
      filterFunctionsForm.setValues(newDataset);
    },
    [filterFunctionsForm.setFieldValue, filterFunctionsForm.values]
  );

  const onSort = useCallback(
    (orders) => {
      const newDataset = { ...filterFunctionsForm.values, orders };
      const queryString = transformDataset(newDataset);
      getFunctions({
        params: {},
        paramsSerializer: () => queryString,
      });
      filterFunctionsForm.setFieldValue('orders', orders);
    },
    [getFunctions, filterFunctionsForm.setFieldValue, filterFunctionsForm.values]
  );

  const onCloseDeleteAlertModal = useCallback(() => {
    setDeleteAlertModal({ open: false, element: null });
  }, [setDeleteAlertModal]);

  const onYesDeleteAlertModal = useCallback(async () => {
    (deleteFunction({ url: `/${deleteAlertModal.element?.id}` }) as ResponsePromise<void>).then(
      () => {
        setDeleteAlertModal({ open: false, element: null });
        setDeleteSuccessModal({ open: true, element: deleteAlertModal.element });
      }
    );
  }, [setDeleteAlertModal, deleteFunction, setDeleteSuccessModal, deleteAlertModal.element]);

  const onNoDeleteAlertModal = useCallback(() => {
    setDeleteAlertModal({ open: false, element: null });
  }, [setDeleteAlertModal]);

  const onCloseDeleteSuccessModal = useCallback(() => {
    const queryString = transformDataset({ ...filterFunctionsForm.values, page: 0 });
    getFunctions({
      params: {},
      paramsSerializer: () => queryString,
    });
    setDeleteSuccessModal({ open: false, element: null });
  }, [getFunctions, setDeleteSuccessModal, filterFunctionsForm.values]);

  const onOkDeleteSuccessModal = useCallback(() => {
    const queryString = transformDataset({ ...filterFunctionsForm.values, page: 0 });
    getFunctions({
      params: {},
      paramsSerializer: () => queryString,
    });
    setDeleteSuccessModal({ open: false, element: null });
  }, [getFunctions, setDeleteSuccessModal, filterFunctionsForm.values]);

  return (
    <Fragment>
      <Breadcrumbs items={CommonFunctionsCrumbs} />
      <ListHeader
        addBtnAriaLabel="Aggiungi nuova funzione"
        addBtnLabel="Aggiungi nuova funzione"
        canAdd={auths.create}
        onAddBtnClick={() => {
          navigate('/sistema/funzioni/nuovo');
        }}
        title="Gestione Funzioni"
      />
      <div className="pmi-my-3">
        <FilterFunctions filterFunctionsForm={filterFunctionsForm} />
      </div>
      {functions.data ? (
        (functions.data as Array<FunctionsClient>)?.length > 0 ? (
          <Table
            onSort={onSort}
            head={headCells}
            orders={filterFunctionsForm.values.orders}
            activeActions={auths}
            page={filterFunctionsForm.values.page}
            rowsPerPage={filterFunctionsForm.values.rowsPerPage}
            body={(functions.data as Array<FunctionsClient>) || []}
            onVisibilityPress={onVisibilityPress}
            onEditPress={onEditPress}
            onTrashPress={onTrashPress}
            count={count || 0}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ariaLabel="Lista Funzioni"
          />
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
      <DeleteAlertModal
        title="Cancellazione Funzione"
        message="Confermare la cancellazione?"
        open={deleteAlertModal.open}
        onClose={onCloseDeleteAlertModal}
        onYes={onYesDeleteAlertModal}
        onNo={onNoDeleteAlertModal}
      />
      <DeleteSuccessModal
        open={deleteSuccessModal.open}
        title="Cancellazione Funzione"
        message={`La cancellazione della funzione ${deleteSuccessModal.element?.id} è avvenuta con successo.`}
        onClose={onCloseDeleteSuccessModal}
        onOk={onOkDeleteSuccessModal}
      />
    </Fragment>
  );
};

FunctionsList.displayName = 'Functions List';

export default FunctionsList;
